import React, { useState, useRef, useEffect } from 'react'
import { Input, Icon, Popover, Spin, Empty, Button } from 'antd'
import _ from 'lodash'
import './CommentInput.scss'

const MENTION_TYPES = {
  CHARACTER: 'character',
  LOCATION: 'location',
  OUTFIT: 'outfit',
  PROP: 'prop'
}

const CommentInput = ({
  rows = 4,
  label = 'Prompt',
  text = '',
  value: propValue,
  onChange,
  disabled = false,
  placeholder = 'Write a comment...',
  characterList = [],
  locationReference = {},
  selectedCharacter = [],
  locationMap = {},
  onOpenLocationSelector,
  onOpenCharacterSelector,
  outfitMap = {},
  outfitsReference = {},
  outfits = [],
  propMap = {},
  propsReference = {}
}) => {
  const incomingValue = propValue !== undefined ? propValue : text
  const [value, setValue] = useState(incomingValue || '')
  const [mentionPopup, setMentionPopup] = useState({
    visible: false,
    type: null,
    query: '',
    cursorPos: 0,
    coords: { top: 0, left: 0 }
  })
  const [inputWidth, setInputWidth] = useState(0)
  const [hoveredTag, setHoveredTag] = useState(null)
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [popoverCoords, setPopoverCoords] = useState({ x: 0, y: 0 })
  const [popoverEntity, setPopoverEntity] = useState(null)

  const containerRef = useRef(null)
  const textareaRef = useRef(null)
  const backdropRef = useRef(null)
  const mirrorRef = useRef(null)
  const isUserTyping = useRef(false)
  const typingTimeout = useRef(null)

  useEffect(() => {
    if (!isUserTyping.current && incomingValue !== value) {
      setValue(incomingValue || '')
    }
  }, [incomingValue])

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        setInputWidth(containerRef.current.offsetWidth)
      }
      updateWidth()
      window.addEventListener('resize', updateWidth)
      return () => window.removeEventListener('resize', updateWidth)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        // Also check if click is inside the portaled popup
        const popover = document.querySelector('.mention-popover-overlay')
        if (popover && popover.contains(e.target)) return

        setMentionPopup(prev => ({ ...prev, visible: false }))
      }
    }

    const handleScrollOutside = e => {
      if (
        e.target instanceof Element &&
        e.target.closest('.mention-popover-overlay')
      ) {
        return
      }

      // Also check container
      if (containerRef.current && containerRef.current.contains(e.target)) {
        return
      }

      setMentionPopup(prev => {
        if (prev.visible) return { ...prev, visible: false }
        return prev
      })
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOutside, { capture: true })
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOutside, {
        capture: true
      })
      if (typingTimeout.current) clearTimeout(typingTimeout.current)
    }
  }, [])

  // Sync scroll between textarea and backdrop
  const handleScroll = e => {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.target.scrollTop
      backdropRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  const getCoordinates = pos => {
    if (!textareaRef.current) return { top: 0, left: 0 }

    const el = textareaRef.current.resizableTextArea.textArea
    const textBeforeCaret = value.substring(0, pos)

    // Create mirror element for measuring
    const mirror = document.createElement('div')
    const style = window.getComputedStyle(el)

    const relevantStyles = [
      'fontFamily',
      'fontSize',
      'fontWeight',
      'fontStyle',
      'fontVariant',
      'lineHeight',
      'textTransform',
      'letterSpacing',
      'wordSpacing',
      'paddingTop',
      'paddingRight',
      'paddingBottom',
      'paddingLeft',
      'borderTopWidth',
      'borderRightWidth',
      'borderBottomWidth',
      'borderLeftWidth',
      'boxSizing',
      'width'
    ]

    relevantStyles.forEach(s => (mirror.style[s] = style[s]))
    mirror.style.position = 'absolute'
    mirror.style.visibility = 'hidden'
    mirror.style.whiteSpace = 'pre-wrap'
    mirror.style.wordWrap = 'break-word'
    mirror.style.overflow = 'hidden'
    mirror.style.top = '0'
    mirror.style.left = '0'

    mirror.textContent = textBeforeCaret

    const span = document.createElement('span')
    span.textContent = '|'
    mirror.appendChild(span)

    document.body.appendChild(mirror)
    const { offsetTop, offsetLeft } = span
    document.body.removeChild(mirror)

    return {
      top: offsetTop - el.scrollTop,
      left: offsetLeft - el.scrollLeft
    }
  }

  const detectMentions = (text, cursorPos) => {
    const textBeforeCursor = text.substring(0, cursorPos)
    const textAfterCursor = text.substring(cursorPos)

    // Check if cursor is inside an existing tag: __tag__
    const tagMatchBefore = textBeforeCursor.match(/(__[^_]*)$/)
    const tagMatchAfter = textAfterCursor.match(/^([^_]*__)/)

    if (tagMatchBefore && tagMatchAfter) {
      const fullTag = tagMatchBefore[1] + tagMatchAfter[1]
      const tagContent = fullTag.replace(/^__|__$/g, '')
      const isLoc = tagContent.startsWith('location')
      const isOutfit = tagContent.startsWith('outfit')
      const isProp = tagContent.startsWith('prop')

      // Do not show mention popup for outfits
      if (isOutfit) {
        setMentionPopup(prev => ({ ...prev, visible: false }))
        return false
      }

      const type = isLoc
        ? MENTION_TYPES.LOCATION
        : isProp
        ? MENTION_TYPES.PROP
        : MENTION_TYPES.CHARACTER
      const query = tagContent
      const coords = getCoordinates(cursorPos - tagMatchBefore[1].length)

      setMentionPopup({
        visible: true,
        type,
        query,
        cursorPos,
        coords
      })
      return true
    }

    // Standard mention detection
    const charMatch = textBeforeCursor.match(/@(\w*)$/)
    // Location match: # or _ followed by digits/dots.
    // Must not be preceded by another _ to avoid matching part of a character tag __char__
    const locMatch = textBeforeCursor.match(/(?:^|[^\w_])[#_]([\d\.]*)$/)

    if (charMatch) {
      const query = charMatch[1]
      const coords = getCoordinates(cursorPos - query.length - 1)
      setMentionPopup({
        visible: true,
        type: MENTION_TYPES.CHARACTER,
        query,
        cursorPos,
        coords
      })
      return true
    } else if (locMatch) {
      const query = locMatch[1]
      const triggerIndex =
        textBeforeCursor.lastIndexOf(locMatch[0]) +
        locMatch[0].indexOf(locMatch[0].match(/[#_]/)[0])
      const coords = getCoordinates(triggerIndex)
      setMentionPopup({
        visible: true,
        type: MENTION_TYPES.LOCATION,
        query,
        cursorPos,
        coords
      })
      return true
    }

    setMentionPopup(prev => ({ ...prev, visible: false }))
    return false
  }

  const handleChange = e => {
    isUserTyping.current = true
    const newText = e.target.value
    setValue(newText)
    onChange && onChange(newText)

    detectMentions(newText, e.target.selectionStart)

    if (typingTimeout.current) clearTimeout(typingTimeout.current)
    typingTimeout.current = setTimeout(() => {
      isUserTyping.current = false
      typingTimeout.current = null
    }, 600)
  }

  const handleCursorMove = e => {
    // Ignore navigation keys
    const navKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'PageUp',
      'PageDown'
    ]
    if (e.type === 'keyup' && navKeys.includes(e.key)) {
      setMentionPopup(prev => ({ ...prev, visible: false }))
      return
    }

    if (!isUserTyping.current) {
      detectMentions(value, e.target.selectionStart)
    }
  }

  const insertTag = (tag, type) => {
    isUserTyping.current = true

    if (!textareaRef.current) return
    const el = textareaRef.current.resizableTextArea.textArea
    const start = el.selectionStart
    const textBefore = value.substring(0, start)
    const textAfter = value.substring(start)

    let matchStart = -1
    let textToReplace_len = 0

    // Check if we are inside an existing tag
    const tagMatchBefore = textBefore.match(/(__[^_]*)$/)
    const tagMatchAfter = textAfter.match(/^([^_]*__)/)

    if (tagMatchBefore && tagMatchAfter) {
      matchStart = textBefore.length - tagMatchBefore[1].length
      textToReplace_len = tagMatchBefore[1].length + tagMatchAfter[1].length
    } else {
      // Standard mention replacement
      let queryMatch
      if (type === MENTION_TYPES.CHARACTER) {
        queryMatch = textBefore.match(/@(\w*)$/)
      } else {
        queryMatch = textBefore.match(/(?:^|[^\w_])[#_]([\d\.]*)$/)
      }

      if (queryMatch) {
        // For locMatch with (?:^|[^\w_]), we need to find the actual trigger index
        const triggerMatch = queryMatch[0].match(/[#@_]/)
        matchStart =
          textBefore.lastIndexOf(queryMatch[0]) +
          queryMatch[0].indexOf(triggerMatch[0])
        textToReplace_len = textBefore.length - matchStart
      }
    }

    if (matchStart === -1) {
      isUserTyping.current = false
      return
    }

    // Resolve mapping for character
    let finalTag = `__${tag}__`
    if (type === MENTION_TYPES.CHARACTER) {
      const mapping = (selectedCharacter || []).find(
        m => (m.current || '').replace(/^__|__$/g, '') === tag
      )
      if (mapping) finalTag = mapping.original
    } else if (type === MENTION_TYPES.LOCATION) {
      if (!tag.startsWith('location')) {
        finalTag = `__location${tag}__`
      } else {
        finalTag = `__${tag}__`
      }
    }

    const newVal =
      value.substring(0, matchStart) +
      finalTag +
      ' ' +
      value.substring(matchStart + textToReplace_len)

    setValue(newVal)
    onChange && onChange(newVal)
    setMentionPopup(prev => ({ ...prev, visible: false }))

    // Refocus and set cursor
    setTimeout(() => {
      el.focus()
      const newPos = matchStart + finalTag.length + 1
      el.setSelectionRange(newPos, newPos)
    }, 0)

    // Clear typing flag after delay to allow round trip
    if (typingTimeout.current) clearTimeout(typingTimeout.current)
    typingTimeout.current = setTimeout(() => {
      isUserTyping.current = false
      typingTimeout.current = null
    }, 600)
  }

  const getFilteredEntities = () => {
    const { type, query } = mentionPopup
    const lowerQuery = query.toLowerCase()

    if (type === MENTION_TYPES.CHARACTER) {
      return (characterList || [])
        .filter(c => {
          const mapping = (selectedCharacter || []).find(
            m => (m.current || '').replace(/^__|__$/g, '') === c.name
          )
          if (!mapping) return false

          const original = (mapping.original || '')
            .replace(/^__|__$/g, '')
            .toLowerCase()
          const current = (mapping.current || '')
            .replace(/^__|__$/g, '')
            .toLowerCase()
          const name = c.name.toLowerCase()

          return (
            original.includes(lowerQuery) ||
            current.includes(lowerQuery) ||
            name.includes(lowerQuery)
          )
        })
        .map(c => ({
          id: c.id,
          name: c.name,
          image: c.attributes?.profile_pic,
          icon: 'user'
        }))
    } else if (type === MENTION_TYPES.LOCATION) {
      return Object.keys(locationMap || {})
        .filter(key => key.toLowerCase().includes(lowerQuery))
        .map(key => {
          const rawKey = key.replace(/^__/, '').replace(/__$/, '')
          const reference = (locationReference || {})[key]
          const image =
            reference?.uri ||
            reference?.url ||
            reference?.image ||
            reference?.src
          return {
            id: key,
            name: rawKey,
            fullTag: key,
            image,
            icon: 'environment'
          }
        })
    } else if (type === MENTION_TYPES.PROP) {
      return Object.keys(propMap || {})
        .filter(key => key.toLowerCase().includes(lowerQuery))
        .map(key => {
          const rawKey = key.replace(/^__/, '').replace(/__$/, '')
          const reference = (propsReference || {})[key]
          const image =
            reference?.uri ||
            reference?.url ||
            reference?.image ||
            reference?.src
          return {
            id: key,
            name: rawKey,
            fullTag: key,
            image,
            icon: 'tool'
          }
        })
    }
    return []
  }

  const handleMouseMove = e => {
    if (!textareaRef.current || popoverVisible) return

    const el = textareaRef.current.resizableTextArea.textArea
    const rect = el.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
  }

  const renderBackdropContent = () => {
    if (!value) return null

    // Split by tags: __...__
    const parts = value.split(/(__[\s\S]+?__)/g)
    const content = parts
      .filter(part => part !== '')
      .map((part, i) => {
        if (part.match(/^__[\s\S]+?__$/)) {
          const tagContent = part.replace(/^__|__$/g, '')
          const isLoc = tagContent.startsWith('location')
          const isOutfit = tagContent.startsWith('outfit')
          const isProp = tagContent.startsWith('prop')

          let entity = null
          if (isLoc) {
            const locDesc = locationMap[part]
            const reference = locationReference[part]
            if (locDesc || reference) {
              entity = {
                name: tagContent,
                subtitle: locDesc,
                image:
                  reference?.uri ||
                  reference?.url ||
                  reference?.image ||
                  reference?.src,
                type: 'location'
              }
            }
          } else if (isOutfit) {
            const outfitDesc = outfitMap[part]
            if (outfitDesc) {
              // Find image from library
              let outfitImage = null
              Object.values(outfitsReference).forEach(outfitMapByChar => {
                const outfitId = outfitMapByChar[part]
                if (outfitId && outfits) {
                  const item = outfits.find(o => (o.tag || o.id) === outfitId)
                  if (item) {
                    outfitImage =
                      item.primary_image ||
                      (item.views && Object.values(item.views)[0])
                  }
                }
              })

              entity = {
                name: tagContent,
                subtitle: outfitDesc,
                image: outfitImage,
                type: 'outfit'
              }
            }
          } else if (isProp) {
            const propDesc = propMap[part]
            const reference = propsReference[part]
            if (propDesc || reference) {
              entity = {
                name: tagContent,
                subtitle: propDesc,
                image:
                  reference?.uri ||
                  reference?.url ||
                  reference?.image ||
                  reference?.src,
                type: 'prop'
              }
            }
          } else {
            const mapping = (selectedCharacter || []).find(
              m => m.original === part || m.current === part
            )
            const resolvedName = mapping
              ? mapping.current.replace(/^__|__$/g, '')
              : tagContent
            const char = (characterList || []).find(
              c => c.name === resolvedName
            )
            if (char) {
              entity = {
                name: char.name,
                image: char.attributes?.profile_pic,
                type: 'character'
              }
            }
          }

          const tagEl = (
            <span
              key={i}
              className={`highlight-tag ${entity ? 'valid' : 'invalid'}`}
            >
              {part}
            </span>
          )

          if (entity) {
            return (
              <Popover
                key={i}
                content={
                  <div className="entity-popover-content">
                    {entity.image && (
                      <img src={entity.image} alt={entity.name} />
                    )}
                    <div className="entity-info">
                      <div className="entity-name">{entity.name}</div>
                      {entity.subtitle && (
                        <div className="entity-subtitle">{entity.subtitle}</div>
                      )}
                    </div>
                  </div>
                }
                title={null}
                trigger="hover"
                mouseEnterDelay={0.3}
                overlayClassName="rich-input-popover"
              >
                {tagEl}
              </Popover>
            )
          }
          return tagEl
        }
        return part
      })

    // Match textarea behavior: if ends with a newline, add a space to make the final line height correct
    const finalContent = value.endsWith('\n') ? [...content, ' '] : content
    return <>{finalContent}</>
  }

  const renderMentionPopup = () => {
    if (!mentionPopup.visible) return null

    const entities = getFilteredEntities()
    const hasResults = entities.length > 0
    const showFooter =
      (mentionPopup.type === MENTION_TYPES.CHARACTER &&
        onOpenCharacterSelector) ||
      (mentionPopup.type === MENTION_TYPES.LOCATION && onOpenLocationSelector)

    return (
      <div className="mention-popup">
        <div className="mention-list">
          {hasResults ? (
            entities.map(item => (
              <div
                key={item.id}
                className="mention-item"
                onClick={() => insertTag(item.name, mentionPopup.type)}
              >
                {item.image ? (
                  <img src={item.image} alt={item.name} />
                ) : (
                  <div className="mention-icon">
                    <Icon type={item.icon} />
                  </div>
                )}
                <span className="mention-name">{item.name}</span>
              </div>
            ))
          ) : (
            <div className="mention-empty">
              <Empty image={null} description="No results" />
            </div>
          )}
        </div>
        {mentionPopup.type === MENTION_TYPES.CHARACTER &&
          onOpenCharacterSelector && (
            <div className="mention-footer">
              <Button
                type="link"
                size="small"
                onClick={onOpenCharacterSelector}
              >
                Open Full Selector...
              </Button>
            </div>
          )}
        {mentionPopup.type === MENTION_TYPES.LOCATION &&
          onOpenLocationSelector && (
            <div className="mention-footer">
              <Button type="link" size="small" onClick={onOpenLocationSelector}>
                Open Full Selector...
              </Button>
            </div>
          )}
      </div>
    )
  }

  return (
    <div className="rich-comment-input" ref={containerRef}>
      <div className="comment-input-header">
        <label>{label}</label>
      </div>

      <div className="input-wrapper">
        <div className="backdrop-container" ref={backdropRef}>
          <div className="backdrop-content">{renderBackdropContent()}</div>
        </div>

        <Input.TextArea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onScroll={handleScroll}
          onKeyUp={handleCursorMove}
          onClick={handleCursorMove}
          readOnly={disabled}
          autosize={{ minRows: rows, maxRows: rows }}
          placeholder={placeholder}
          className="rich-textarea"
        />

        <Popover
          visible={mentionPopup.visible}
          trigger="contextMenu"
          placement="bottomLeft"
          content={renderMentionPopup()}
          overlayClassName="mention-popover-overlay"
          overlayStyle={{ width: inputWidth - 2 }} // -2 for borders
          autoAdjustOverflow={false}
        >
          <div className="popover-anchor" />
        </Popover>
      </div>
    </div>
  )
}

export default CommentInput
